import { getAge, getTodayOrFutureDate } from '../dateFunctions';

import { MIN_EMAIL, MIN_PHONE, NHS_LENGTH } from './commonConstants';
import { AGE_UNDER_SIXTEEN, UNIDENTIFIED_UNIQUE_IDENTIFIER_TYPE } from './commonErrorConstants';
import {
    getCharMinMessage,
    getDateMaxMessage,
    getFormatMessage,
    getInvalidMessage,
    getIsDigitLengthMessage,
    getNotMatchingMessage,
    getReqMessage,
    getSelectMessage
} from './errorMessageFunctions';
import { nhsNumberChecksum } from './nhsNumberChecksum';
import { regExps } from './validationPatterns';

// Remove all Unicode whitespace characters pasted from common external tools.
export const normalizePhoneNumber = (phoneNumber = '') => phoneNumber.replace(regExps.REMOVE_SPACES, '');

export const validatePhone = (phoneNumber, errors, errorField) => {
    if (phoneNumber) {
        if (phoneNumber?.trim().length < MIN_PHONE) {
            return {
                ...errors,
                [errorField]: {
                    error: true,
                    message: getCharMinMessage('Phone number', MIN_PHONE)
                }
            };
        } else if (!regExps.PHONE_R.test(phoneNumber)) {
            return {
                ...errors,
                [errorField]: { error: true, message: getFormatMessage('Phone number') }
            };
        }
    }
    return errors;
};

export const validateEmail = (emailAddress, errors, errorField = 'emailAddress') => {
    if (emailAddress) {
        if (emailAddress?.trim().length < MIN_EMAIL) {
            return {
                ...errors,
                [errorField]: {
                    error: true,
                    message: getCharMinMessage('Email address', MIN_EMAIL)
                }
            };
        } else if (!regExps.EMAIL_R.test(emailAddress)) {
            return {
                ...errors,
                [errorField]: { error: true, message: getFormatMessage('Email') }
            };
        } else {
            const userName = emailAddress.split('@')[0];
            if (userName.length > 64) {
                return {
                    ...errors,
                    [errorField]: { error: true, message: getFormatMessage('Email') }
                };
            }
        }
    }
    return errors;
};

export const validatePostcode = (postcode, errors, errorField = 'postcode') => {
    if (postcode) {
        if (!regExps.POSTCODE_R.test(postcode)) {
            return {
                ...errors,
                [errorField]: { error: true, message: getFormatMessage('Postcode') }
            };
        }
    }
    return errors;
};

export const validateContractId = (contractId, errors, errorField = 'contractId') => {
    if (!contractId)
        return {
            ...errors,
            [errorField]: { error: true, message: getSelectMessage('contract') }
        };
    return errors;
};

export const validateServiceId = (serviceId, errors, errorField = 'serviceId') => {
    if (!serviceId)
        return {
            ...errors,
            [errorField]: { error: true, message: getSelectMessage('service') }
        };
    return errors;
};

export const validateTeamId = (teamId, errors, errorField = 'teamId') => {
    if (!teamId)
        return {
            ...errors,
            [errorField]: { error: true, message: getSelectMessage('team') }
        };
    return errors;
};

export const validateDateOfBirth = (dateOfBirth, errors) => {
    if (!dateOfBirth) {
        return {
            ...errors,
            dateOfBirth: { error: true, message: getReqMessage('Date of birth') }
        };
    } else if (getTodayOrFutureDate(dateOfBirth)) {
        return {
            ...errors,
            dateOfBirth: { error: true, message: getDateMaxMessage("today's date") }
        };
    } else if (getAge(dateOfBirth) < 16) {
        return {
            ...errors,
            dateOfBirth: { error: true, message: AGE_UNDER_SIXTEEN }
        };
    }
    return errors;
};

export const validateGender = (genderId, errors) => {
    if (!genderId) {
        return {
            ...errors,
            genderId: { error: true, message: getSelectMessage('gender') }
        };
    }
    return errors;
};

export const validateUniqueIdentifier = (type, uniqueIdentifier, repeat, errors) => {
    let newErrors = { ...errors };

    if (type === 'TEMPORARY') return errors;
    else if (type === 'NINO') {
        if (!uniqueIdentifier) {
            newErrors = {
                ...newErrors,
                uniqueIdentifier: {
                    error: true,
                    message: getReqMessage('National Insurance number')
                }
            };
        } else if (!regExps.NINO_R.test(uniqueIdentifier)) {
            newErrors = {
                ...newErrors,
                uniqueIdentifier: {
                    error: true,
                    message: getFormatMessage('National Insurance number')
                }
            };
        }
        if (!repeat) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: {
                    error: true,
                    message: getReqMessage('Repeat National Insurance number')
                }
            };
        } else if (!regExps.NINO_R.test(repeat)) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: {
                    error: true,
                    message: getFormatMessage('Repeat National Insurance number')
                }
            };
        } else if (uniqueIdentifier !== repeat) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: {
                    error: true,
                    message: getNotMatchingMessage('National Insurance numbers')
                }
            };
        }
        return newErrors;
    } else if (type === 'NHS_NUMBER') {
        if (!uniqueIdentifier) {
            newErrors = {
                ...newErrors,
                uniqueIdentifier: { error: true, message: getReqMessage('NHS number') }
            };
        } else if (!regExps.NUMBERS_R.test(uniqueIdentifier)) {
            newErrors = {
                ...newErrors,
                uniqueIdentifier: { error: true, message: getFormatMessage('NHS number') }
            };
        } else if (uniqueIdentifier.length !== NHS_LENGTH) {
            newErrors = {
                ...newErrors,
                uniqueIdentifier: {
                    error: true,
                    message: getIsDigitLengthMessage('NHS number', NHS_LENGTH)
                }
            };
        } else if (!nhsNumberChecksum(uniqueIdentifier)) {
            newErrors = {
                ...newErrors,
                uniqueIdentifier: { error: true, message: getInvalidMessage('NHS number') }
            };
        }

        if (!repeat) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: { error: true, message: getReqMessage('Repeat NHS number') }
            };
        } else if (uniqueIdentifier !== repeat) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: {
                    error: true,
                    message: getNotMatchingMessage('NHS numbers')
                }
            };
        } else if (!regExps.NUMBERS_R.test(repeat)) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: {
                    error: true,
                    message: getFormatMessage('Repeat NHS number')
                }
            };
        } else if (repeat.length !== NHS_LENGTH) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: {
                    error: true,
                    message: getIsDigitLengthMessage('Repeat NHS number', NHS_LENGTH)
                }
            };
        } else if (!nhsNumberChecksum(repeat)) {
            newErrors = {
                ...newErrors,
                repeatUniqueIdentifier: {
                    error: true,
                    message: getInvalidMessage('Repeat NHS number')
                }
            };
        }
        return newErrors;
    } else {
        return {
            ...newErrors,
            uniqueIdentifier: { error: true, message: UNIDENTIFIED_UNIQUE_IDENTIFIER_TYPE }
        };
    }
};
